import { Input, PolicyDispatchingPermission, assets } from '@pulseops/common'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { ActivityIndicator, StyleProp, View, ViewStyle, Text } from 'react-native'
import { ZIO } from '@mxt/zio'
import { pipe } from 'fp-ts/function'

import { borderedInputStyle } from './styles/policyDispatchingStyles'
import PolicyDispatchingDialog from './components/PolicyDispatchingDialog'
import { PolicyDispatchingService } from './services/PolicyDispatchingService'
import { getPermissions } from './utils/policyDispatchingUtils'

type FormValues = {
  agentNumber: string
  agentName: string
  status: string
  type: string
  dateAppointed: string
  dateTerminate: string
  gaBp: string
  agentSalesUnitCode: string
  area: string
  branch: string
  agentSalesBranch: string
  saleZone: string
  phoneNumber1: string
  phoneNumber2: string
  address: string
  leaderCode: string
  leaderName: string
  leaderStatus: string
  leaderType: string
  leaderDateAppointed: string
  leaderDateTerminate: string
  leaderBranch: string
  leaderGaBp: string
  leaderPhoneNumber1: string
  leaderPhoneNumber2: string
  leaderAddress: string
  mum: string
  rdm: string
  rdo: string
}

const defaultValues: FormValues = {
  agentNumber: '',
  agentName: '',
  status: '',
  type: '',
  dateAppointed: '',
  dateTerminate: '',
  gaBp: '',
  agentSalesUnitCode: '',
  area: '',
  branch: '',
  agentSalesBranch: '',
  saleZone: '',
  phoneNumber1: '',
  phoneNumber2: '',
  address: '',
  leaderCode: '',
  leaderName: '',
  leaderStatus: '',
  leaderType: '',
  leaderDateAppointed: '',
  leaderDateTerminate: '',
  leaderBranch: '',
  leaderGaBp: '',
  leaderPhoneNumber1: '',
  leaderPhoneNumber2: '',
  leaderAddress: '',
  mum: '',
  rdm: '',
  rdo: ''
}

type ControlledInputProps = {
  control: any
  name: keyof FormValues
  title: string
  required?: boolean
  containerStyle?: StyleProp<ViewStyle>
  disabled?: boolean
}

const ControlledInput = ({ control, name, title, required, containerStyle, disabled = true }: ControlledInputProps) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
        <Input
          title={title}
          onChange={onChange}
          onBlur={onBlur}
          value={value}
          errorMessage={error?.message}
          containerStyle={containerStyle as any}
          required={required}
          inputStyle={borderedInputStyle}
          filled
          alwayShowUnderline
          disabled={disabled}
        />
      )}
    />
  )
}

const AgentInformation = ({ control }: { control: any }) => {
  return (
    <View style={{ display: 'flex', flexDirection: 'column', gap: 16 } as any}>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="agentName" title="Họ và tên" />
        </View>
        <View style={styles.column_1_2} />
      </View>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="status" title="Tình trạng" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="type" title="Loại" />
        </View>
      </View>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="dateAppointed" title="Ngày gia nhập" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="dateTerminate" title="Ngày kết thúc" />
        </View>
      </View>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="gaBp" title="GA/BP" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="agentSalesUnitCode" title="Nhóm" />
        </View>
      </View>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="area" title="Khu vực" />
        </View>
        <View style={styles.column_1_2} />
      </View>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="branch" title="Văn phòng" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="agentSalesBranch" title="Ban" />
        </View>
      </View>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="saleZone" title="Vùng" />
        </View>
        <View style={styles.column_1_2} />
      </View>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="phoneNumber1" title="Điện thoại liên lạc 1" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="phoneNumber2" title="Điện thoại liên lạc 2" />
        </View>
      </View>
      <View style={{ width: '100%' } as any}>
        <ControlledInput control={control} name="address" title="Địa chỉ" />
      </View>
    </View>
  )
}

const LeaderInformation = ({ control, mum, rdm, rdo }: { control: any; mum: string; rdm: string; rdo: string }) => {
  return (
    <View style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 24 } as any}>
      <Text style={{ fontSize: '18px', fontWeight: '700', marginBottom: 8 } as any}>Thông tin trưởng nhóm</Text>
      <View style={styles.row}>
        <View style={{ width: '20%' } as any}>
          <ControlledInput control={control} name="leaderCode" title="Trưởng nhóm" />
        </View>
        <View style={{ width: '80%', marginTop: 22 } as any}>
          <ControlledInput control={control} name="leaderName" title="" />
        </View>
      </View>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="leaderStatus" title="Tình trạng" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="leaderType" title="Loại" />
        </View>
      </View>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="leaderDateAppointed" title="Ngày gia nhập" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="leaderDateTerminate" title="Ngày kết thúc" />
        </View>
      </View>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="leaderBranch" title="Văn phòng" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="leaderGaBp" title="GA/BP" />
        </View>
      </View>
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="leaderPhoneNumber1" title="Điện thoại liên lạc 1" />
        </View>
        <View style={styles.column_1_2}>
          <ControlledInput control={control} name="leaderPhoneNumber2" title="Điện thoại liên lạc 2" />
        </View>
      </View>
      <View style={{ width: '100%' } as any}>
        <ControlledInput control={control} name="leaderAddress" title="Địa chỉ" />
      </View>
      <View style={{ display: 'flex', flexDirection: 'column', gap: 8 } as any}>
        <View style={styles.row}>
          <Text style={{ fontWeight: 'bold' } as any}>MUM</Text>
          {mum ? <Text>{mum}</Text> : <Text style={{ fontStyle: 'italic' } as any}>Không tìm thấy thông tin MUM</Text>}
        </View>
        <View style={styles.row}>
          <Text style={{ fontWeight: 'bold' } as any}>RDO</Text>
          {rdo ? <Text>{rdo}</Text> : <Text style={{ fontStyle: 'italic' } as any}>Không tìm thấy thông tin RDO</Text>}
        </View>
        <View style={styles.row}>
          <Text style={{ fontWeight: 'bold' } as any}>RDM</Text>
          {rdm ? <Text>{rdm}</Text> : <Text style={{ fontStyle: 'italic' } as any}>Không tìm thấy thông tin RDM</Text>}
        </View>
      </View>
    </View>
  )
}

export default function PDAgentInformationScreen() {
  const { control, handleSubmit, setValue, watch } = useForm<FormValues>({ defaultValues })
  const [isLoading, setIsLoading] = React.useState(false)
  const [modalInfo, setModalInfo] = React.useState({ title: '', message: '' })
  const [openModal, setOpenModal] = React.useState(false)
  const permissions = getPermissions()
  const allowSearch = permissions.includes(PolicyDispatchingPermission.QuickSearchSearch)

  const clearAgentDetails = () => {
    setValue('agentName', '')
    setValue('status', '')
    setValue('type', '')
    setValue('dateAppointed', '')
    setValue('dateTerminate', '')
    setValue('gaBp', '')
    setValue('agentSalesUnitCode', '')
    setValue('area', '')
    setValue('branch', '')
    setValue('agentSalesBranch', '')
    setValue('saleZone', '')
    setValue('phoneNumber1', '')
    setValue('phoneNumber2', '')
    setValue('address', '')
    setValue('leaderCode', '')
    setValue('leaderName', '')
    setValue('leaderStatus', '')
    setValue('leaderType', '')
    setValue('leaderDateAppointed', '')
    setValue('leaderDateTerminate', '')
    setValue('leaderBranch', '')
    setValue('leaderGaBp', '')
    setValue('leaderPhoneNumber1', '')
    setValue('leaderPhoneNumber2', '')
    setValue('leaderAddress', '')
    setValue('mum', '')
    setValue('rdm', '')
    setValue('rdo', '')
  }

  const handleSearch = handleSubmit((values) => {
    if (!values.agentNumber) {
      setModalInfo({ title: 'Tìm kiếm', message: 'Vui lòng nhập mã số đại lý trước khi tìm kiếm.' })
      setOpenModal(true)
      return
    }

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.getAgentInfo(values.agentNumber),
      ZIO.tap((response) =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          if (!('body' in response)) {
            clearAgentDetails()
            setModalInfo({ title: 'Tìm kiếm', message: 'Không tìm thấy thông tin được yêu cầu!' })
            setOpenModal(true)
            return
          }

          const info = response.body
          setValue('agentName', info.agentName || '')
          setValue('status', info.status || '')
          setValue('type', info.type || '')
          setValue('dateAppointed', info.dateAppointed || '')
          setValue('dateTerminate', info.dateTerminate || '')
          setValue('gaBp', info.gaBp || '')
          setValue('agentSalesUnitCode', info.agentSalesUnitCode || '')
          setValue('area', info.area || '')
          setValue('branch', info.branch || '')
          setValue('agentSalesBranch', info.agentSalesBranch || '')
          setValue('saleZone', info.saleZone || '')
          setValue('phoneNumber1', info.phoneNumber1 || '')
          setValue('phoneNumber2', info.phoneNumber2 || '')
          setValue('address', info.address || '')
          setValue('leaderCode', info.groupLeader?.leaderCode || '')
          setValue('leaderName', info.groupLeader?.leaderName || '')
          setValue('leaderStatus', info.groupLeader?.status || '')
          setValue('leaderType', info.groupLeader?.type || '')
          setValue('leaderDateAppointed', info.groupLeader?.dateAppointed || '')
          setValue('leaderDateTerminate', info.groupLeader?.dateTerminate || '')
          setValue('leaderBranch', info.groupLeader?.branch || '')
          setValue('leaderGaBp', info.groupLeader?.gaBp || '')
          setValue('leaderPhoneNumber1', info.groupLeader?.phoneNumber1 || '')
          setValue('leaderPhoneNumber2', info.groupLeader?.phoneNumber2 || '')
          setValue('leaderAddress', info.groupLeader?.address || '')
          setValue('mum', info.groupLeader?.mum || '')
          setValue('rdm', info.groupLeader?.rdm || '')
          setValue('rdo', info.groupLeader?.rdo || '')
        })
      ),
      ZIO.catchAll((error) =>
        ZIO.effectTotal(() => {
          console.error('Unable to search agent info', error)
          setIsLoading(false)
          clearAgentDetails()
          setModalInfo({ title: 'Tìm kiếm thất bại', message: 'Đã có lỗi xảy ra. Vui lòng thử lại sau.' })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  })

  return (
    <View style={{ ...styles.container, position: 'relative' } as any}>
      <PolicyDispatchingDialog
        visible={openModal}
        title={modalInfo.title}
        message={modalInfo.message}
        onClose={() => setOpenModal(false)}
      />
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#ED1B2E" />
        </View>
      )}
      <Text style={styles.title}>Tìm nhanh</Text>
      <View style={styles.wrapper}>
        <Text style={styles.subTitle}>Thông tin đại lý</Text>
        {!!watch('dateTerminate') && <Text style={styles.result}>Đại lý đã ngưng hoạt động</Text>}
        <Text style={styles.noted}>(* thông tin cần nhập)</Text>
        <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 16, marginBottom: 24 } as any}>
          <View style={{ width: '20%' } as any}>
            <ControlledInput
              control={control}
              name="agentNumber"
              title="Mã số"
              containerStyle={{ flex: 1 }}
              disabled={false}
              required
            />
          </View>
          <button
            style={{
              marginTop: 24,
              minHeight: 40,
              ...(isLoading || !allowSearch ? styles.disabledButton : styles.submitButton)
            }}
            type="button"
            onClick={handleSearch}
            disabled={isLoading || !allowSearch}
          >
            <span style={styles.mr_5}>
              <assets.SearchWhite />
            </span>
            Tìm
          </button>
        </View>
        <AgentInformation control={control} />
        <LeaderInformation control={control} mum={watch('mum')} rdm={watch('rdm')} rdo={watch('rdo')} />
      </View>
    </View>
  )
}

const styles: Record<string, any> = {
  container: {
    paddingTop: 0,
    paddingRight: 40,
    paddingBottom: 30,
    paddingLeft: 40,
    height: '100%',
    overflow: 'auto'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '20px'
  },
  wrapper: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  },
  subTitle: {
    color: '#fff',
    fontSize: '18px',
    fontWeight: 'bold',
    paddingTop: 4,
    paddingRight: 12,
    paddingBottom: 4,
    paddingLeft: 12,
    borderRadius: 4,
    backgroundColor: '#e7000b',
    marginBottom: 16,
    width: 'fit-content'
  },
  noted: {
    fontSize: '12px',
    color: '#6a7282',
    fontStyle: 'italic',
    marginBottom: 12,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end'
  },
  result: {
    color: '#894b00',
    backgroundColor: '#fefce8',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#ffdf20',
    borderRadius: 4,
    paddingTop: 8,
    paddingRight: 16,
    paddingBottom: 8,
    paddingLeft: 16,
    fontSize: '14px',
    width: 'fit-content',
    marginBottom: 12
  },
  submitButton: {
    backgroundColor: '#ED1B2E',
    color: '#fff',
    borderWidth: 0,
    paddingTop: 6,
    paddingRight: 16,
    paddingBottom: 6,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600'
  },
  disabledButton: {
    backgroundColor: '#e5e7eb',
    color: '#9ca3af',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#d1d5db',
    paddingTop: 6,
    paddingRight: 16,
    paddingBottom: 6,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600',
    cursor: 'not-allowed',
    opacity: 0.7
  },
  mr_5: {
    marginRight: '5px'
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
    width: '100%'
  },
  column_1_2: {
    width: 'calc(100% / 2 - 12px)'
  },
  loadingOverlay: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 2
  }
}
